import { ParseTreeListener, TerminalNode } from 'antlr4/tree/Tree';

import { isValidRegExp } from '@neptune/shared/common-util';

import { SearchQueryTerm, SearchQueryTermType, SearchQueryTermWithoutPositions } from '../types';

import {
  SearchRegexQueryParserErrorCode,
  SearchRegexQueryParserNodeSymbol,
  SearchRegexQueryParserResult,
} from './types';

type ExpectedToken = 'operand' | 'operator-or-end';

export class SearchRegexQueryParserListener
  implements ParseTreeListener, SearchRegexQueryParserResult
{
  constructor(private symbolicNames: string[]) {}

  status: 'valid' | 'invalid' = 'valid';
  query: SearchQueryTerm[] = [];
  errorCode?: SearchRegexQueryParserErrorCode;
  lastValidQuery: SearchQueryTerm[] = [];

  private lastCompleteQuery: SearchQueryTerm[] = [];
  private parenDepth = 0;
  private expected: ExpectedToken = 'operand';

  visitTerminal(node: TerminalNode): void {
    if (this.status !== 'valid') {
      return;
    }

    const symbol = this.getNodeSymbol(node);

    if (symbol === SearchRegexQueryParserNodeSymbol.EOF) {
      this.finalize();
      return;
    }

    switch (symbol) {
      case SearchRegexQueryParserNodeSymbol.CRITERION:
        this.handleCriterion(node);
        return;
      case SearchRegexQueryParserNodeSymbol.AND:
        this.handleBinaryOperator('AND', node);
        return;
      case SearchRegexQueryParserNodeSymbol.OR:
        this.handleBinaryOperator('OR', node);
        return;
      case SearchRegexQueryParserNodeSymbol.NOT:
        this.handleNotOperator(node);
        return;
      case SearchRegexQueryParserNodeSymbol.LPAREN:
        this.handleLeftParen(node);
        return;
      case SearchRegexQueryParserNodeSymbol.RPAREN:
        this.handleRightParen(node);
        return;
      default:
        return;
    }
  }

  visitErrorNode(): void {
    void 0;
  }

  enterEveryRule(): void {
    void 0;
  }

  exitEveryRule(): void {
    void 0;
  }

  private handleCriterion(node: TerminalNode): void {
    if (this.expected !== 'operand') {
      this.invalidate(SearchRegexQueryParserErrorCode.MALFORMED_QUERY);
      return;
    }

    const value = this.unescapeSpecialCharacters(node.getText());

    if (!isValidRegExp(value)) {
      this.invalidate(SearchRegexQueryParserErrorCode.QUERY_CONTAINS_INVALID_REGEXP);
      return;
    }

    this.appendQueryTerm({ type: SearchQueryTermType.CRITERION, value }, node);
    this.expected = 'operator-or-end';
    this.updateProgress();
  }

  private handleBinaryOperator(value: 'AND' | 'OR', node: TerminalNode): void {
    if (this.expected !== 'operator-or-end') {
      this.invalidate(SearchRegexQueryParserErrorCode.MALFORMED_QUERY);
      return;
    }

    this.appendQueryTerm({ type: SearchQueryTermType.OPERATOR, value }, node);
    this.expected = 'operand';
    this.updateProgress();
  }

  private handleNotOperator(node: TerminalNode): void {
    if (this.expected !== 'operand') {
      this.invalidate(SearchRegexQueryParserErrorCode.MALFORMED_QUERY);
      return;
    }

    this.appendQueryTerm({ type: SearchQueryTermType.OPERATOR, value: 'NOT' }, node);
    this.expected = 'operand';
    this.updateProgress();
  }

  private handleLeftParen(node: TerminalNode): void {
    if (this.expected !== 'operand') {
      this.invalidate(SearchRegexQueryParserErrorCode.MALFORMED_QUERY);
      return;
    }

    this.appendQueryTerm({ type: SearchQueryTermType.GROUP_START, value: '(' }, node);
    this.parenDepth += 1;
    this.expected = 'operand';
    this.updateProgress();
  }

  private handleRightParen(node: TerminalNode): void {
    if (this.parenDepth === 0) {
      this.invalidate(SearchRegexQueryParserErrorCode.UNEXPECTED_CLOSING_PAREN);
      return;
    }

    if (this.expected !== 'operator-or-end') {
      this.invalidate(SearchRegexQueryParserErrorCode.MALFORMED_QUERY);
      return;
    }

    this.appendQueryTerm({ type: SearchQueryTermType.GROUP_END, value: ')' }, node);
    this.parenDepth -= 1;
    this.expected = 'operator-or-end';
    this.updateProgress();
  }

  private finalize(): void {
    if (this.parenDepth > 0) {
      this.invalidate(SearchRegexQueryParserErrorCode.MISSING_CLOSING_PAREN);
    }
  }

  private appendQueryTerm(term: SearchQueryTermWithoutPositions, node: TerminalNode): void {
    const symbol = node.getSymbol();

    this.query.push({
      ...term,
      startPosition: symbol.start,
      endPosition: symbol.stop + 1,
    });
  }

  private updateProgress(): void {
    this.lastValidQuery = [...this.query];

    if (this.parenDepth === 0 && this.expected === 'operator-or-end') {
      this.lastCompleteQuery = [...this.query];
    }
  }

  private invalidate(errorCode: SearchRegexQueryParserErrorCode): void {
    this.status = 'invalid';
    this.errorCode = errorCode;
    this.lastValidQuery = [...this.lastCompleteQuery];
  }

  private unescapeSpecialCharacters(text: string): string {
    return text
      .replace(/(?<!\\)\\x20/g, ' ')
      .replace(/\\\\x20/g, '\\x20')
      .replace(/^\\!/g, '!');
  }

  private getNodeSymbol(node: TerminalNode): SearchRegexQueryParserNodeSymbol {
    const symbolIndex = node.getSymbol().type;

    if (symbolIndex === -1) {
      return SearchRegexQueryParserNodeSymbol.EOF;
    }

    return this.symbolicNames[symbolIndex] as SearchRegexQueryParserNodeSymbol;
  }
}

export function mapSearchRegexParserErrorToErrorCode(
  error: string | undefined,
): SearchRegexQueryParserErrorCode | undefined {
  if (!error) {
    return undefined;
  }

  const normalized = error.toLowerCase();

  const closingParenTokenPatterns = ["' )'", "')'"];
  const containsClosingParenToken = closingParenTokenPatterns.some((pattern) =>
    normalized.includes(pattern),
  );

  if (
    (normalized.includes('extraneous input') && containsClosingParenToken) ||
    (normalized.includes('mismatched input') && containsClosingParenToken) ||
    (normalized.includes('no viable alternative at input') && containsClosingParenToken)
  ) {
    return SearchRegexQueryParserErrorCode.UNEXPECTED_CLOSING_PAREN;
  }

  return SearchRegexQueryParserErrorCode.MALFORMED_QUERY;
}
